import React, { useRef, useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import withBackend from '../../backend/withBackend';
import { setUser } from '../../actions';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Snackbar, SnackbarContent } from '@mui/material';
import { v4 } from 'uuid';
import useTurnstile from '../../custom-hooks/useTurnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const GiveawayModal = ({ isOpen, onClose, onSubmitted, isSubmitted, currentLanguage, engine, setUser }) => {
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openCoupon, setOpenCoupon] = useState(false);
  const [isGoogleLoginFlow, setIsGoogleLoginFlow] = useState(false);
  const [isEmailLinkSubmitted, setIsEmailLinkSubmitted] = useState(false);
  const [initializeTurnstile, setInitializeTurnstile] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const shouldShowSubmittedState = isSubmitted || isEmailLinkSubmitted;
  const { containerRef, token, isLoading: isTurnstileLoading, error: turnstileError, reset } = useTurnstile({
    siteKey: initializeTurnstile ? TURNSTILE_SITE_KEY : null,
    theme: 'auto',
    size: 'normal',
  });

  useEffect(() => {
    const handleClickOutside = event => {
      if (shouldShowSubmittedState) return;
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose, shouldShowSubmittedState]);

  useEffect(() => {
    if (!isOpen) {
      setIsEmailLinkSubmitted(false);
    }
  }, [isOpen]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailInputFocus = () => {
    if (TURNSTILE_SITE_KEY && !token) {
      if (!initializeTurnstile) {
        setInitializeTurnstile(true);
        setShowTurnstile(true);
      } else if (!showTurnstile) {
        setShowTurnstile(true);
      }
    }
  };

  useEffect(() => {
    if (token && showTurnstile) {
      setShowTurnstile(false);
    }
  }, [token, showTurnstile]);

  useEffect(() => {
    if (turnstileError && !token && initializeTurnstile && TURNSTILE_SITE_KEY) {
      setShowTurnstile(true);
    }
  }, [turnstileError, token, initializeTurnstile]);

  const handleSubmit = async (e) => {
    if (loading) return;

    navigator.clipboard.writeText("DECOR10");
    setOpenCoupon(true);

    if (!email) {
      setEmailError(t('message.error.emptyEmail'));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t('message.error.validEmail'));
      return;
    }

    if (TURNSTILE_SITE_KEY && !token) {
      message.error(t('message.error.completeCaptcha'));
      return;
    }

    setLoading(true);
    setIsGoogleLoginFlow(false);

    try {
      if (email.toLowerCase().endsWith('@gmail.com')) {
        setIsGoogleLoginFlow(true);
        await handleSubmitWithGoogle(email);
        return;
      }

      const subscribed = await subscribeToGiveaway(email);
      if (!subscribed) {
        message.error(t('message.error.subscribeError'));
        return;
      }

      handleSubmitWithEmailLink(email);
      setIsEmailLinkSubmitted(true);
      onSubmitted(true);
      message.success(t('message.success.subscribed'));
    } catch (error) {
      message.error(t('message.error.subscribeError'));
    } finally {
      if (TURNSTILE_SITE_KEY) {
        reset();
      }
      setLoading(false);
      setIsGoogleLoginFlow(false);
    }
  };

  const cancelGoogleOneTap = () => {
    try {
      if (window?.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    } catch (error) {
      console.warn('Failed to cancel Google One Tap:', error);
    }
  };

  const subscribeToGiveaway = async (safeEmail) => {
    const apiUrl = 'https://decorify.lemonsqueezy.com/email-subscribe/external';
    const targetName = `lemon-signup-${Date.now()}`;
    const iframe = document.createElement('iframe');
    iframe.name = targetName;
    iframe.style.display = 'none';

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = apiUrl;
    form.target = targetName;
    form.style.display = 'none';

    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.value = safeEmail;
    form.appendChild(emailInput);

    document.body.appendChild(iframe);
    document.body.appendChild(form);

    try {
      form.submit();
      return true;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        form.remove();
        iframe.remove();
      }, 1000);
    }
  };

  const handleSubmitWithGoogle = async (emailForSubscribe) => {
    const response = await engine.signInWithGoogle();
    const oauthEmail = response?.user?.email || emailForSubscribe;
    const userSnapshot = await engine.getUser(response.user.uid);

    if (userSnapshot.data()) {
      setUser(userSnapshot.data());
      cancelGoogleOneTap();
      onSubmitted(true);
      onClose();
      navigate('/app');
      if (oauthEmail) {
        try {
          const subscribed = await subscribeToGiveaway(oauthEmail);
          if (!subscribed) {
            message.warning(t('giveAway.subscribeRetryAfterLogin'));
          }
        } catch (subscribeError) {
          message.warning(t('giveAway.subscribeRetryAfterLogin'));
        }
      }
      return;
    }

    const randomCode = Math.floor(Math.random() * 900000) + 100000;
    const randomTrialCode = Math.floor(Math.random() * 900000) + 100000;
    const refCode = localStorage.getItem('refCode') || '';
    const initialUserData = {
      uid: response.user.uid,
      role: "USER",
      email: response.user.email,
      joinedDate: new Date().getTime(),
      lastLoginDate: null,
      firstName: response.user.displayName || '',
      lastName: "",
      phone: null,
      phoneCode: 84,
      phoneShort: "VN",
      profilePicUrl: null,
      companyName: null,
      country: null,
      birthDate: null,
      gender: null,
      address: null,
      city: null,
      status: "incomplete",
      isActivated: true,
      hasSubscription: false,
      emailVerified: true,
      proTrial: process.env.REACT_APP_PRO_TRIAL,
      basicTrial: process.env.REACT_APP_BASIC_TRIAL,
      upscaleTrial: process.env.REACT_APP_UPSCALE_TRIAL,
      pro: false,
      basic: true,
      trial: true,
      monthlyBasicCode: `REOK ${randomCode}`,
      yearlyBasicCode: `YB ${randomCode}`,
      monthlyProCode: `MP ${randomCode}`,
      yearlyProCode: `YP ${randomCode}`,
      trialCode: `REOK ${randomTrialCode}`,
      proCredits: 0,
      basicCredits: 30,
      refCode,
    };

    await engine.addUser(response.user.uid, initialUserData);

    const randomUUID = v4();
    const refLink = `https://decorify.pro/vi/?ref=${randomUUID}`;
    const affMember = {
      uid: response.user.uid,
      email: response.user.email,
      code: randomUUID,
      refLink,
      isActive: true,
      createdAt: new Date().toISOString(),
    };
    try {
      await engine.addAffMembers(affMember);
    } catch (error) {
      console.warn('Affiliate member creation failed:', error);
    }

    const newUserSnapshot = await engine.getUser(response.user.uid);
    setUser(newUserSnapshot.data());
    cancelGoogleOneTap();
    onSubmitted(true);
    onClose();
    navigate('/app');
    if (oauthEmail) {
      try {
        const subscribed = await subscribeToGiveaway(oauthEmail);
        if (!subscribed) {
          message.warning(t('giveAway.subscribeRetryAfterLogin'));
        }
      } catch (subscribeError) {
        message.warning(t('giveAway.subscribeRetryAfterLogin'));
      }
    }
  };

  const handleSubmitWithEmailLink = (email) => {
    try {

      const baseUrl = window.location.origin;
      const redirectUrl = currentLanguage === 'en'
        ? `${baseUrl}/verify-email/?email=${email}`
        : `${baseUrl}/${currentLanguage}/verify-email/?email=${email}`;

      const actionCodeSettings = {
        url: redirectUrl,
        handleCodeInApp: true,
      };

      engine
        .sendSignInLinkToEmail(email, actionCodeSettings, token)
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.log(error);
        });

    } catch (error) {
      console.log(error);
    }
  };

  const handleGoogleFromSuccessMessage = async () => {
    if (loading) return;

    setLoading(true);
    setIsGoogleLoginFlow(true);

    try {
      await handleSubmitWithGoogle(email);
    } finally {
      setLoading(false);
      setIsGoogleLoginFlow(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none' }}>
        <div ref={popupRef}
          className="relative bg-black rounded-lg shadow-lg p-5 max-w-[412px] mx-auto text-white transition-all duration-300 ease-in-out"
          style={{ transform: isOpen ? 'scale(1)' : 'scale(0.9)', opacity: isOpen ? 1 : 0, border: "1px solid #343434" }}>
          <button
            onClick={onClose}
            className="absolute right-2 text-gray-400 hover:text-gray-200 top-1"
          >
            ✕
          </button>
          <div className="flex justify-center mb-4">
            <svg width="50" height="50" viewBox="0 0 0.875 0.875" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="bounce-rotate-animation">
              <path fill="#d32f2f" d="M0.729 0.813H0.146a0.042 0.042 0 0 1 -0.042 -0.042V0.354h0.667v0.417c0 0.023 -0.019 0.042 -0.042 0.042" />
              <path fill="#f44336" d="M0.813 0.354H0.063v-0.125C0.063 0.206 0.081 0.188 0.104 0.188h0.667c0.023 0 0.042 0.019 0.042 0.042z" />
              <path fill="#ff8f00" d="M0.396 0.354h0.083V0.813H0.396zm0.125 -0.292 -0.125 0.125h0.083l0.125 -0.125z" />
              <path fill="#ffc107" d="M0.396 0.354h0.083V0.188l-0.125 -0.125H0.271l0.125 0.125z" />
            </svg>
          </div>

          <h2 className="text-center text-2xl font-bold mb-2">
            <div className="mx-auto relative z-[10] text-center mx-auto md:text-center label-giveaway">
              <span>{t('giveAway.title')}</span>
            </div>
          </h2>

          <p className="text-center text-gray-400 mb-6">
            {t('giveAway.subTitle')}
          </p>
          {shouldShowSubmittedState ? (
            <div className="flex flex-col items-center justify-center mb-2">
              <hr className="w-full border-t border-gray-700 mb-6" />
              <div className="flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-white font-semibold">&nbsp;{t('giveAway.emailSent')}</span>
              </div>
              <h2 className="text-center text-2xl font-bold">
                <div className="mx-auto relative z-[10] text-center mx-auto md:text-center label-giveaway">
                  <span>{t('giveAway.emailSentTitle')}</span>&nbsp;🎉
                </div>
              </h2>
              <p className="text-gray-400 text-center">
                {t('giveAway.emailSentSubPrefix')}{' '}
                <button
                  type="button"
                  onClick={handleGoogleFromSuccessMessage}
                  disabled={loading}
                  className="text-blue-400 underline underline-offset-2 hover:text-blue-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {t('giveAway.googleWord')}
                </button>{' '}
                {t('giveAway.emailSentSubSuffix')}
              </p>
            </div>
          ) : (
            <>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleEmailInputFocus}
                onClick={handleEmailInputFocus}
                placeholder={t('giveAway.enterYourEmail')}
                className="w-full px-4 py-3 rounded-md border border-gray-700 mb-3 focus:outline-none text-white placeholder-gray-500" style={{ backgroundColor: '#151515' }}
              />
              {emailError && <p className="text-red text-sm mb-4">{emailError}</p>}
              {TURNSTILE_SITE_KEY && (
                <>
                  <div
                    style={{
                      display: showTurnstile ? 'flex' : 'none',
                      justifyContent: 'center',
                      marginBottom: '16px',
                    }}
                  >
                    <div ref={containerRef} />
                  </div>
                  {turnstileError && showTurnstile && !isTurnstileLoading && (
                    <p className="text-red text-sm mb-4 text-center">{turnstileError}</p>
                  )}
                </>
              )}

              <button
                onClick={handleSubmit}
                className="upgrade-button w-full"
                style={{ minWidth: '100%', height: '52px', borderRadius: '8px', fontSize: '15px' }}
                disabled={loading || (TURNSTILE_SITE_KEY && (isTurnstileLoading || !token))}
              >
                {loading
                  ? (isGoogleLoginFlow ? t('giveAway.signingInWithGoogle') : t('giveAway.submitting'))
                  : t('giveAway.notifyMe')}
              </button>
              <div className="text-center text-sm text-gray-500 mt-4 mb-2">
                {t('tos.agree')}
                <a
                  className="agree-service" style={{ color: "#c2c2c2" }}
                  href={'/tos'}
                  target="_blank"
                >
                  {t('footer.terms')}
                </a>
                {' '}&{' '}
                <a
                  className="agree-service" style={{ color: "#c2c2c2" }}
                  href={'/privacy'}
                  target="_blank"
                >
                  {t('footer.privacy')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
      <Snackbar
        open={openCoupon}
        onClose={() => setOpenCoupon(false)}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 99999 }}
      >
        <SnackbarContent
          sx={{
            background: '#1a1a1a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          }}
          message={
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>✓</span>
              <span>
                Code <strong style={{ color: '#f0a500' }}>DECOR10</strong> copied — paste it at checkout!
              </span>
            </span>
          }
        />
      </Snackbar>
    </>
  );
};

export default compose(
  connect(null, { setUser }),
  withBackend
)(GiveawayModal);
